import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSearch,
  faPlus,
  faCheck,
  faEdit,
  faTrash,
  faRefresh,
  faProjectDiagram,
} from '@fortawesome/free-solid-svg-icons';
import WorkflowService from '@frontend/AppSettings/services/WorkflowService';
import { Workflow } from '@frontend/Models/Workflow/WorkflowModels';

interface WorkflowManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedWorkflowId: string;
  onSelect: (workflowId: string) => void;
  onWorkflowsChanged?: () => Promise<void> | void;
}

type WorkflowMessage = { type: 'success' | 'error' | ''; text: string };

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(17, 24, 39, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20,
  },
  modal: {
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: 960,
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '24px 28px 20px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafbfc',
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 18,
    marginRight: 16,
    flexShrink: 0,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.025em',
  },
  headerSubtitle: {
    margin: '6px 0 0',
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: 'none',
    background: '#f3f4f6',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    transition: 'all 0.15s ease',
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: 24,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1.3fr 1fr',
    gap: 24,
    height: '100%',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  cardHeader: {
    padding: '18px 20px',
    borderBottom: '1px solid #f3f4f6',
    background: '#fafbfc',
  },
  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardTitleIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: '#eef2ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4338ca',
    fontSize: 12,
  },
  cardBody: {
    padding: 20,
    flex: 1,
    overflow: 'auto',
    maxHeight: '50vh',
  },
  searchRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
    padding: '11px 14px 11px 40px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
    fontSize: 14,
    color: '#111827',
    outline: 'none',
    transition: 'all 0.15s ease',
  },
  searchIcon: {
    position: 'absolute' as const,
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    fontSize: 14,
  },
  refreshBtn: {
    padding: '10px 16px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#374151',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.15s ease',
  },
  workflowItem: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    background: '#ffffff',
    transition: 'all 0.15s ease',
  },
  workflowItemSelected: {
    border: '2px solid #4338ca',
    background: '#faf5ff',
    boxShadow: '0 0 0 4px rgba(67, 56, 202, 0.1)',
  },
  workflowHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  workflowName: {
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
    margin: 0,
    lineHeight: 1.4,
  },
  workflowActions: {
    display: 'flex',
    gap: 6,
    flexShrink: 0,
  },
  actionBtn: {
    padding: '6px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#374151',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s ease',
  },
  useBtn: {
    background: '#4338ca',
    color: '#ffffff',
    border: 'none',
  },
  deleteBtn: {
    color: '#dc2626',
    borderColor: '#fee2e2',
  },
  milestonesRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap' as const,
  },
  milestone: {
    padding: '5px 12px',
    borderRadius: 20,
    background: '#f3f4f6',
    color: '#374151',
    fontSize: 12,
    fontWeight: 500,
    border: '1px solid #e5e7eb',
  },
  milestoneSelected: {
    background: '#eef2ff',
    color: '#4338ca',
    borderColor: '#c7d2fe',
  },
  noMilestones: {
    fontSize: 12,
    color: '#9ca3af',
    fontStyle: 'italic' as const,
  },
  formInput: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
    fontSize: 14,
    color: '#111827',
    marginBottom: 14,
    outline: 'none',
    transition: 'all 0.15s ease',
    boxSizing: 'border-box' as const,
  },
  milestoneInputRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 14,
  },
  addBtn: {
    padding: '10px 18px',
    borderRadius: 10,
    border: 'none',
    background: '#4338ca',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.15s ease',
  },
  milestonesPreview: {
    background: '#f9fafb',
    borderRadius: 10,
    padding: 14,
    border: '1px solid #e5e7eb',
    marginBottom: 16,
  },
  milestonesLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: 10,
  },
  milestoneChip: {
    padding: '6px 12px',
    borderRadius: 20,
    background: '#eef2ff',
    color: '#4338ca',
    fontSize: 13,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid #c7d2fe',
    marginRight: 8,
    marginBottom: 8,
  },
  removeBtn: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(67, 56, 202, 0.15)',
    color: '#4338ca',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    transition: 'all 0.15s ease',
  },
  createActions: {
    display: 'flex',
    gap: 10,
    marginTop: 6,
  },
  primaryBtn: {
    flex: 1,
    padding: '12px 20px',
    borderRadius: 10,
    border: 'none',
    background: '#4338ca',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'all 0.15s ease',
  },
  secondaryBtn: {
    padding: '12px 20px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#374151',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  message: {
    margin: '0 24px 16px',
    padding: '12px 16px',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: 14,
    fontWeight: 500,
  },
  messageSuccess: {
    background: '#ecfdf5',
    color: '#065f46',
    border: '1px solid #a7f3d0',
  },
  messageError: {
    background: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '32px 20px',
    color: '#6b7280',
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
    color: '#9ca3af',
    fontSize: 20,
  },
};

const WorkflowManagerModal: React.FC<WorkflowManagerModalProps> = ({
  isOpen,
  onClose,
  selectedWorkflowId,
  onSelect,
  onWorkflowsChanged,
}) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [message, setMessage] = useState<WorkflowMessage>({ type: '', text: '' });
  const [workflowForm, setWorkflowForm] = useState<{
    name: string;
    milestones: string[];
    currentMilestone: string;
  }>({ name: '', milestones: [], currentMilestone: '' });
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);

  const fetchWorkflows = async (): Promise<void> => {
    try {
      setLoading(true);
      const data: Workflow[] = await WorkflowService.getWorkflows();
      const normalized = (data || []).map((w: any) => ({
        ...w,
        workflowid: w.workflowid ?? w.workflow_id ?? w.workflow_id_string ?? undefined,
      }));
      setWorkflows(normalized);
      // eslint-disable-next-line no-console
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setMessage({ type: 'error', text: 'Failed to load workflows.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchWorkflows();
      setMessage({ type: '', text: '' });
    }
  }, [isOpen]);

  const resetWorkflowForm = (): void => {
    setWorkflowForm({ name: '', milestones: [], currentMilestone: '' });
    setEditingWorkflow(null);
  };

  const handleWorkflowCreate = async (): Promise<void> => {
    if (!workflowForm.name.trim()) {
      setMessage({ type: 'error', text: 'Please enter a workflow name.' });
      return;
    }
    try {
      await WorkflowService.addWorkflow({
        name: workflowForm.name.trim(),
        milestones: workflowForm.milestones || [],
      });
      setMessage({ type: 'success', text: 'Workflow created successfully!' });
      resetWorkflowForm();
      await fetchWorkflows();
      onWorkflowsChanged && (await onWorkflowsChanged());
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      console.error('Error adding workflow:', err);
      setMessage({ type: 'error', text: 'Failed to create workflow.' });
    }
  };

  const handleWorkflowUpdate = async (): Promise<void> => {
    if (!editingWorkflow) return;
    if (!editingWorkflow.name.trim()) {
      setMessage({ type: 'error', text: 'Please enter a workflow name.' });
      return;
    }
    try {
      await WorkflowService.updateWorkflow(editingWorkflow.workflowid, {
        name: editingWorkflow.name.trim(),
        milestones: editingWorkflow.milestones || [],
      });
      setMessage({ type: 'success', text: 'Workflow updated successfully!' });
      resetWorkflowForm();
      await fetchWorkflows();
      onWorkflowsChanged && (await onWorkflowsChanged());
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      console.error('Error updating workflow:', err);
      setMessage({ type: 'error', text: 'Failed to update workflow.' });
    }
  };

  const handleWorkflowDelete = async (workflowId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    try {
      await WorkflowService.deleteWorkflow(workflowId);
      setMessage({ type: 'success', text: 'Workflow deleted successfully!' });
      await fetchWorkflows();
      onWorkflowsChanged && (await onWorkflowsChanged());
      if (selectedWorkflowId === workflowId) {
        onSelect('');
      }
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      console.error('Error deleting workflow:', err);
      setMessage({ type: 'error', text: 'Failed to delete workflow.' });
    }
  };

  const filteredWorkflows = useMemo(
    () =>
      workflows.filter((w) => {
        const q = search.toLowerCase();
        return (
          (w.name || '').toLowerCase().includes(q) ||
          (w.milestones || []).some((m: string) => (m || '').toLowerCase().includes(q))
        );
      }),
    [search, workflows]
  );

  const addMilestoneToForm = (): void => {
    if (workflowForm.currentMilestone?.trim()) {
      setWorkflowForm((prev) => ({
        ...prev,
        milestones: [...prev.milestones, prev.currentMilestone.trim()],
        currentMilestone: '',
      }));
    }
  };

  const addMilestoneToEditing = (): void => {
    if (editingWorkflow && (editingWorkflow as any).currentMilestone?.trim()) {
      setEditingWorkflow({
        ...editingWorkflow,
        milestones: [...(editingWorkflow.milestones || []), (editingWorkflow as any).currentMilestone.trim()],
        currentMilestone: '',
      } as Workflow);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={styles.headerIcon}>
              <FontAwesomeIcon icon={faProjectDiagram} />
            </div>
            <div style={styles.headerContent}>
              <h2 style={styles.headerTitle}>Workflow Manager</h2>
              <p style={styles.headerSubtitle}>
                Create, edit, and manage workflows. Select one to attach to this matter.
              </p>
            </div>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e5e7eb';
              e.currentTarget.style.color = '#111827';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Message Banner */}
        {message.text && (
          <div
            style={{
              ...styles.message,
              ...(message.type === 'success' ? styles.messageSuccess : styles.messageError),
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FontAwesomeIcon icon={message.type === 'success' ? faCheck : faTimes} />
              {message.text}
            </span>
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 4 }}
              onClick={() => setMessage({ type: '', text: '' })}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Body */}
        <div style={styles.body}>
          <div style={styles.grid}>
            {/* Left: Workflows List */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardTitleIcon}>
                    <FontAwesomeIcon icon={faProjectDiagram} />
                  </span>
                  Available Workflows
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#6b7280',
                      background: '#f3f4f6',
                      padding: '4px 10px',
                      borderRadius: 20,
                    }}
                  >
                    {filteredWorkflows.length} total
                  </span>
                </h3>
              </div>
              <div style={styles.cardBody}>
                {/* Search */}
                <div style={styles.searchRow}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Search workflows..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={styles.searchInput}
                    />
                  </div>
                  <button
                    style={styles.refreshBtn}
                    onClick={() => {
                      setSearch('');
                      fetchWorkflows();
                    }}
                  >
                    <FontAwesomeIcon icon={faRefresh} />
                    Refresh
                  </button>
                </div>

                {/* List */}
                {loading ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>
                      <FontAwesomeIcon icon={faRefresh} className="fa-spin" />
                    </div>
                    <p style={{ margin: 0 }}>Loading workflows...</p>
                  </div>
                ) : filteredWorkflows.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>
                      <FontAwesomeIcon icon={faProjectDiagram} />
                    </div>
                    <p style={{ margin: 0, fontWeight: 500 }}>No workflows found</p>
                    <p style={{ margin: '6px 0 0', fontSize: 13 }}>Create one using the form on the right.</p>
                  </div>
                ) : (
                  <div>
                    {filteredWorkflows.map((w) => {
                      const isSelected = selectedWorkflowId === w.workflowid;
                      const isEditing = editingWorkflow?.workflowid === w.workflowid;

                      return (
                        <div
                          key={w.workflowid}
                          style={{
                            ...styles.workflowItem,
                            ...(isSelected ? styles.workflowItemSelected : {}),
                          }}
                        >
                          {isEditing && editingWorkflow ? (
                            // Editing Mode
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              <input
                                type="text"
                                value={editingWorkflow.name}
                                onChange={(e) =>
                                  setEditingWorkflow((prev) =>
                                    prev ? { ...prev, name: e.target.value } : prev
                                  )
                                }
                                style={{ ...styles.formInput, marginBottom: 0 }}
                                placeholder="Workflow name"
                              />
                              <div style={styles.milestonesRow}>
                                {(editingWorkflow.milestones || []).map((m: string, idx: number) => (
                                  <span key={`${m}-${idx}`} style={styles.milestoneChip}>
                                    {m}
                                    <button
                                      style={styles.removeBtn}
                                      onClick={() =>
                                        setEditingWorkflow((prev) =>
                                          prev
                                            ? {
                                                ...prev,
                                                milestones: (prev.milestones || []).filter(
                                                  (_: string, i: number) => i !== idx
                                                ),
                                              }
                                            : prev
                                        )
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                  type="text"
                                  placeholder="Add milestone..."
                                  value={(editingWorkflow as any).currentMilestone || ''}
                                  onChange={(e) =>
                                    setEditingWorkflow((prev) =>
                                      prev ? { ...prev, currentMilestone: e.target.value } : prev
                                    )
                                  }
                                  onKeyDown={(e) => e.key === 'Enter' && addMilestoneToEditing()}
                                  style={{ ...styles.formInput, flex: 1, marginBottom: 0 }}
                                />
                                <button style={styles.addBtn} onClick={addMilestoneToEditing}>
                                  <FontAwesomeIcon icon={faPlus} />
                                </button>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button style={styles.primaryBtn} onClick={handleWorkflowUpdate}>
                                  <FontAwesomeIcon icon={faCheck} />
                                  Save Changes
                                </button>
                                <button
                                  style={styles.secondaryBtn}
                                  onClick={() => setEditingWorkflow(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Display Mode
                            <>
                              <div style={styles.workflowHeader}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <h4 style={styles.workflowName}>{w.name}</h4>
                                </div>
                                <div style={styles.workflowActions}>
                                  <button
                                    style={{ ...styles.actionBtn, ...styles.useBtn }}
                                    onClick={() => {
                                      onSelect(w.workflowid);
                                      onClose();
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCheck} />
                                    Select
                                  </button>
                                  <button
                                    style={styles.actionBtn}
                                    onClick={() => setEditingWorkflow({ ...w })}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  <button
                                    style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                                    onClick={() => handleWorkflowDelete(w.workflowid)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                              </div>
                              <div style={styles.milestonesRow}>
                                {(w.milestones || []).length > 0 ? (
                                  (w.milestones || []).map((m: string, idx: number) => (
                                    <span
                                      key={`${w.workflowid}-${idx}`}
                                      style={{
                                        ...styles.milestone,
                                        ...(isSelected ? styles.milestoneSelected : {}),
                                      }}
                                    >
                                      {m}
                                    </span>
                                  ))
                                ) : (
                                  <span style={styles.noMilestones}>No milestones defined</span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Create Form - Redesigned for intuitive UX */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <span style={{ ...styles.cardTitleIcon, background: '#dcfce7', color: '#16a34a' }}>
                    <FontAwesomeIcon icon={faPlus} />
                  </span>
                  Build Your Workflow
                </h3>
              </div>
              <div style={styles.cardBody}>
                {/* Step 1: Workflow Name */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: workflowForm.name ? '#4338ca' : '#e5e7eb',
                      color: workflowForm.name ? '#fff' : '#9ca3af',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>1</span>
                    <label style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                      Name your workflow
                    </label>
                  </div>
                  <input
                    type="text"
                    placeholder="e.g., Client Onboarding, Patent Filing, Contract Review..."
                    value={workflowForm.name}
                    onChange={(e) => setWorkflowForm((prev) => ({ ...prev, name: e.target.value }))}
                    style={{
                      ...styles.formInput,
                      marginBottom: 0,
                      fontSize: 15,
                      padding: '14px 16px',
                    }}
                  />
                </div>

                {/* Step 2: Add Milestones */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: workflowForm.milestones.length > 0 ? '#4338ca' : '#e5e7eb',
                      color: workflowForm.milestones.length > 0 ? '#fff' : '#9ca3af',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>2</span>
                    <label style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                      Define the steps (milestones)
                    </label>
                  </div>
                  <p style={{ margin: '0 0 12px 34px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                    Add each stage of your workflow in order. These become checkpoints to track progress.
                  </p>

                  {/* Visual Timeline of Milestones */}
                  {workflowForm.milestones.length > 0 && (
                    <div style={{
                      background: '#f8fafc',
                      borderRadius: 12,
                      padding: 16,
                      marginBottom: 14,
                      marginLeft: 34,
                      border: '1px solid #e2e8f0',
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        {workflowForm.milestones.map((m: string, idx: number) => (
                          <div key={`${m}-${idx}`} style={{ display: 'flex', alignItems: 'stretch' }}>
                            {/* Timeline connector */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              marginRight: 12,
                              width: 24,
                            }}>
                              <div style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                background: '#4338ca',
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                zIndex: 1,
                              }}>{idx + 1}</div>
                              {idx < workflowForm.milestones.length - 1 && (
                                <div style={{
                                  width: 2,
                                  flex: 1,
                                  background: '#c7d2fe',
                                  minHeight: 16,
                                }} />
                              )}
                            </div>
                            {/* Milestone content */}
                            <div style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              background: '#fff',
                              border: '1px solid #e2e8f0',
                              borderRadius: 10,
                              padding: '10px 14px',
                              marginBottom: idx < workflowForm.milestones.length - 1 ? 8 : 0,
                              transition: 'all 0.15s ease',
                            }}>
                              <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 500 }}>{m}</span>
                              <button
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 6,
                                  border: 'none',
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 10,
                                  transition: 'all 0.15s ease',
                                }}
                                onClick={() =>
                                  setWorkflowForm((prev) => ({
                                    ...prev,
                                    milestones: prev.milestones.filter((_, i) => i !== idx),
                                  }))
                                }
                                title="Remove milestone"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add milestone input */}
                  <div style={{ display: 'flex', gap: 10, marginLeft: 34 }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input
                        type="text"
                        placeholder={workflowForm.milestones.length === 0 
                          ? "e.g., Initial Review" 
                          : `Step ${workflowForm.milestones.length + 1}: e.g., ${
                              ['Document Collection', 'Legal Analysis', 'Client Approval', 'Filing', 'Completion'][workflowForm.milestones.length % 5]
                            }`
                        }
                        value={workflowForm.currentMilestone}
                        onChange={(e) =>
                          setWorkflowForm((prev) => ({ ...prev, currentMilestone: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addMilestoneToForm();
                          }
                        }}
                        style={{
                          ...styles.formInput,
                          marginBottom: 0,
                          paddingRight: 90,
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        right: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 11,
                        color: '#9ca3af',
                        pointerEvents: 'none',
                      }}>
                        Press Enter ↵
                      </span>
                    </div>
                    <button
                      style={{
                        ...styles.addBtn,
                        opacity: workflowForm.currentMilestone.trim() ? 1 : 0.5,
                      }}
                      onClick={addMilestoneToForm}
                      disabled={!workflowForm.currentMilestone.trim()}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>

                  {/* Empty state hint */}
                  {workflowForm.milestones.length === 0 && (
                    <div style={{
                      marginLeft: 34,
                      marginTop: 12,
                      padding: '14px 16px',
                      background: '#fef3c7',
                      borderRadius: 10,
                      border: '1px solid #fcd34d',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                    }}>
                      <span style={{ fontSize: 16 }}>💡</span>
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                          Tip: Think of the journey
                        </p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a16207', lineHeight: 1.5 }}>
                          What stages does a matter go through from start to finish? Add them in order above.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div style={{
                  height: 1,
                  background: '#e5e7eb',
                  margin: '20px 0',
                }} />

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    style={{
                      ...styles.primaryBtn,
                      flex: 1,
                      opacity: workflowForm.name.trim() ? 1 : 0.6,
                      cursor: workflowForm.name.trim() ? 'pointer' : 'not-allowed',
                    }}
                    onClick={handleWorkflowCreate}
                    disabled={!workflowForm.name.trim()}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    Create Workflow
                  </button>
                  {(workflowForm.name || workflowForm.milestones.length > 0) && (
                    <button style={styles.secondaryBtn} onClick={resetWorkflowForm}>
                      Start Over
                    </button>
                  )}
                </div>

                {/* Validation hint */}
                {!workflowForm.name.trim() && (
                  <p style={{
                    margin: '12px 0 0',
                    fontSize: 12,
                    color: '#9ca3af',
                    textAlign: 'center',
                  }}>
                    Enter a workflow name to continue
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowManagerModal;
